import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../contexts/AuthContext';
import ProfessionalHeader from '../components/ProfessionalHeader';
import PostFittingLaunchMonitor from '../components/PostFittingLaunchMonitor';
import {
  createReview,
  getReviewsForProfile,
  getRecommendationsForProfile,
  PostFittingReview,
  LaunchMonitorReviewData,
} from '../services/postFittingReviewService';
import toast from 'react-hot-toast';
import { logEvent, newSessionId } from '../services/telemetryService';

// ─── Types ──────────────────────────────────────────────────────────────────

type EquipmentTab = 'driver' | 'iron' | 'wedge';

interface UniqueHead {
  key: string;
  head_brand: string;
  head_model: string;
  head_loft?: number;
  head_score?: number;
  head_id?: number;
}

interface UniqueShaft {
  key: string;
  shaft_brand: string;
  shaft_model: string;
  shaft_flex: string;
  shaft_weight?: number;
  shaft_id?: number;
}

interface RankedHead extends UniqueHead {
  rank: number;
  score: number;
}

interface RankedShaft extends UniqueShaft {
  rank: number;
  score: number;
}

interface LMEntry {
  id: string;
  clubKey: string;
  clubLabel: string;
  data: LaunchMonitorReviewData;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function deduplicateHeads(heads: any[]): UniqueHead[] {
  const seen = new Set<string>();
  const result: UniqueHead[] = [];
  for (const h of heads) {
    const key = `${h.head_brand}|${h.head_model}|${h.head_loft ?? ''}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push({
        key,
        head_brand: h.head_brand || '',
        head_model: h.head_model || '',
        head_loft: h.head_loft,
        head_score: h.head_score,
        head_id: h.head_id,
      });
    }
  }
  return result;
}

function deduplicateShafts(shafts: any[]): UniqueShaft[] {
  const seen = new Set<string>();
  const result: UniqueShaft[] = [];
  for (const s of shafts) {
    const key = `${s.shaft_brand}|${s.shaft_model}|${s.shaft_flex}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push({
        key,
        shaft_brand: s.shaft_brand || '',
        shaft_model: s.shaft_model || '',
        shaft_flex: s.shaft_flex || '',
        shaft_weight: s.shaft_weight,
        shaft_id: s.shaft_id,
      });
    }
  }
  return result;
}

function extractHeadsAndShafts(
  tabData: any,
  tab: EquipmentTab
): { heads: UniqueHead[]; headShaftMap: Record<string, UniqueShaft[]> } {
  if (!tabData) return { heads: [], headShaftMap: {} };

  if (tab === 'wedge') {
    const types = tabData.wedge_types || {};
    const allWedges: any[] = [
      ...(types.gap || []),
      ...(types.sand || []),
      ...(types.lob || []),
    ];
    const heads = deduplicateHeads(
      allWedges.map(w => ({
        head_brand: w.manufacturer || '',
        head_model: w.model || '',
        head_loft: w.loft,
        head_score: w.wedge_head_score,
        head_id: w.wedge_id || w.id,
      }))
    );
    return { heads, headShaftMap: {} };
  }

  const apiHeads = tabData.heads || [];
  const heads = deduplicateHeads(apiHeads);

  // Build per-head shaft map so shaft choices update when heads are selected/deselected
  const headShaftMap: Record<string, UniqueShaft[]> = {};
  for (const apiHead of apiHeads) {
    const key = `${apiHead.head_brand}|${apiHead.head_model}|${apiHead.head_loft ?? ''}`;
    if (!headShaftMap[key]) {
      headShaftMap[key] = deduplicateShafts(apiHead.shafts || []);
    }
  }

  return { heads, headShaftMap };
}

// ─── Sortable item component ─────────────────────────────────────────────────

interface SortableItemProps {
  id: string;
  rank: number;
  label: string;
  sublabel: string;
  score: number;
  onScoreChange: (score: number) => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, rank, label, sublabel, score, onScoreChange }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 mb-2 select-none"
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0 touch-none"
        title="Drag to reorder"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Rank badge */}
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-green/15 text-green-ink text-xs font-bold flex items-center justify-center">
        #{rank}
      </span>

      {/* Item info */}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 text-sm truncate">{label}</div>
        {sublabel && <div className="text-xs text-gray-500 truncate">{sublabel}</div>}
      </div>

      {/* Score buttons — full width row on mobile so they don't overflow */}
      <div className="w-full sm:w-auto flex items-center gap-0.5 pl-10 sm:pl-0">
        <span className="text-xs text-gray-500 mr-1.5">Score:</span>
        {[...Array(10)].map((_, i) => {
          const val = i + 1;
          return (
            <button
              key={val}
              onClick={() => onScoreChange(val)}
              className={`w-6 h-6 text-xs font-medium rounded transition-colors ${
                score === val
                  ? 'bg-green-ink text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-green/15 hover:text-green-ink'
              }`}
            >
              {val}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const PostFittingReviewPage: React.FC = () => {
  const { profileId } = useParams<{ profileId: string }>();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<EquipmentTab>('driver');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessionIdRef = useRef<string>(newSessionId());

  // Log review page opened
  useEffect(() => {
    logEvent({
      session_id: sessionIdRef.current,
      event_type: 'post_fit_review_opened',
      metadata: { came_from: 'results' },
    });
  }, []);

  // Recommendations data per tab
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [recommendations, setRecommendations] = useState<Record<EquipmentTab, any>>(
    { driver: null, iron: null, wedge: null }
  );

  // Extracted unique heads per tab, plus per-head shaft map
  const [tabHeads, setTabHeads] = useState<Record<EquipmentTab, UniqueHead[]>>(
    { driver: [], iron: [], wedge: [] }
  );
  const [headShaftMap, setHeadShaftMap] = useState<Record<EquipmentTab, Record<string, UniqueShaft[]>>>(
    { driver: {}, iron: {}, wedge: {} }
  );

  // Phase: 'select' → 'rank'
  const [phase, setPhase] = useState<'select' | 'rank'>('select');

  // Phase 1 selection state (per tab)
  const [selectedHeadKeys, setSelectedHeadKeys] = useState<Record<EquipmentTab, Set<string>>>(
    { driver: new Set(), iron: new Set(), wedge: new Set() }
  );
  const [selectedShaftKeys, setSelectedShaftKeys] = useState<Record<EquipmentTab, Set<string>>>(
    { driver: new Set(), iron: new Set(), wedge: new Set() }
  );

  // Phase 2 ranking state (per tab)
  const [rankedHeads, setRankedHeads] = useState<Record<EquipmentTab, RankedHead[]>>(
    { driver: [], iron: [], wedge: [] }
  );
  const [rankedShafts, setRankedShafts] = useState<Record<EquipmentTab, RankedShaft[]>>(
    { driver: [], iron: [], wedge: [] }
  );

  // LM data — up to 5 per-club entries
  const [showLmForm, setShowLmForm] = useState(false);
  const [lmEntries, setLmEntries] = useState<LMEntry[]>([]);

  // Existing reviews (to show "already submitted" state)
  const [existingReviews, setExistingReviews] = useState<PostFittingReview[]>([]);

  const pid = parseInt(profileId || '0');

  // Derive available shafts for each tab based on which heads are selected
  const availableShafts = useMemo(() => {
    const result: Record<EquipmentTab, UniqueShaft[]> = { driver: [], iron: [], wedge: [] };
    for (const tab of ['driver', 'iron', 'wedge'] as EquipmentTab[]) {
      const seenKeys = new Set<string>();
      const shafts: UniqueShaft[] = [];
      selectedHeadKeys[tab].forEach(headKey => {
        (headShaftMap[tab][headKey] || []).forEach(s => {
          if (!seenKeys.has(s.key)) {
            seenKeys.add(s.key);
            shafts.push(s);
          }
        });
      });
      result[tab] = shafts;
    }
    return result;
  }, [selectedHeadKeys, headShaftMap]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Load data
  useEffect(() => {
    if (!pid) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [recsData, reviewsData] = await Promise.all([
          getRecommendationsForProfile(pid),
          getReviewsForProfile(pid),
        ]);
        const recs = recsData.recommendations || { driver: null, iron: null, wedge: null };
        setRecommendations(recs);
        setExistingReviews(reviewsData);

        // Extract heads + per-head shaft map for each tab
        const newHeads: Record<EquipmentTab, UniqueHead[]> = { driver: [], iron: [], wedge: [] };
        const newHeadShaftMap: Record<EquipmentTab, Record<string, UniqueShaft[]>> = { driver: {}, iron: {}, wedge: {} };
        for (const tab of ['driver', 'iron', 'wedge'] as EquipmentTab[]) {
          const { heads, headShaftMap: hsm } = extractHeadsAndShafts(recs[tab], tab);
          newHeads[tab] = heads;
          newHeadShaftMap[tab] = hsm;
        }
        setTabHeads(newHeads);
        setHeadShaftMap(newHeadShaftMap);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [pid]);

  // Toggle head selection (max 5)
  const toggleHead = useCallback((tab: EquipmentTab, key: string) => {
    setSelectedHeadKeys(prev => {
      const current = new Set(prev[tab]);
      if (current.has(key)) {
        current.delete(key);
      } else if (current.size < 5) {
        current.add(key);
      }
      return { ...prev, [tab]: current };
    });
  }, []);

  // Toggle shaft selection (max 5)
  const toggleShaft = useCallback((tab: EquipmentTab, key: string) => {
    setSelectedShaftKeys(prev => {
      const current = new Set(prev[tab]);
      if (current.has(key)) {
        current.delete(key);
      } else if (current.size < 5) {
        current.add(key);
      }
      return { ...prev, [tab]: current };
    });
  }, []);

  // Move from select → rank phase: populate ranked arrays
  const proceedToRank = useCallback(() => {
    const newRankedHeads: Record<EquipmentTab, RankedHead[]> = { driver: [], iron: [], wedge: [] };
    const newRankedShafts: Record<EquipmentTab, RankedShaft[]> = { driver: [], iron: [], wedge: [] };

    for (const tab of ['driver', 'iron', 'wedge'] as EquipmentTab[]) {
      const headKeys = selectedHeadKeys[tab];
      const shaftKeys = selectedShaftKeys[tab];

      newRankedHeads[tab] = tabHeads[tab]
        .filter(h => headKeys.has(h.key))
        .map((h, i) => ({ ...h, rank: i + 1, score: 7 }));

      // Use availableShafts (already computed from selected heads) to filter shaft selections
      newRankedShafts[tab] = availableShafts[tab]
        .filter(s => shaftKeys.has(s.key))
        .map((s, i) => ({ ...s, rank: i + 1, score: 7 }));
    }

    setRankedHeads(newRankedHeads);
    setRankedShafts(newRankedShafts);
    setPhase('rank');
    setLmEntries([]); // reset LM entries when entering rank phase
  }, [selectedHeadKeys, selectedShaftKeys, tabHeads, availableShafts]);

  // Drag end for heads
  const handleHeadDragEnd = useCallback((event: DragEndEvent, tab: EquipmentTab) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRankedHeads(prev => {
      const items = prev[tab];
      const oldIndex = items.findIndex(h => h.key === active.id);
      const newIndex = items.findIndex(h => h.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reordered = arrayMove(items, oldIndex, newIndex).map((h, i) => ({
        ...h,
        rank: i + 1,
      }));
      return { ...prev, [tab]: reordered };
    });
  }, []);

  // Drag end for shafts
  const handleShaftDragEnd = useCallback((event: DragEndEvent, tab: EquipmentTab) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setRankedShafts(prev => {
      const items = prev[tab];
      const oldIndex = items.findIndex(s => s.key === active.id);
      const newIndex = items.findIndex(s => s.key === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reordered = arrayMove(items, oldIndex, newIndex).map((s, i) => ({
        ...s,
        rank: i + 1,
      }));
      return { ...prev, [tab]: reordered };
    });
  }, []);

  // Update head score
  const updateHeadScore = useCallback((tab: EquipmentTab, key: string, score: number) => {
    setRankedHeads(prev => ({
      ...prev,
      [tab]: prev[tab].map(h => h.key === key ? { ...h, score } : h),
    }));
  }, []);

  // Update shaft score
  const updateShaftScore = useCallback((tab: EquipmentTab, key: string, score: number) => {
    setRankedShafts(prev => ({
      ...prev,
      [tab]: prev[tab].map(s => s.key === key ? { ...s, score } : s),
    }));
  }, []);

  const hasExistingReview = (tab: EquipmentTab) =>
    existingReviews.some(r => r.equipment_type === tab);

  // Save review
  const handleSave = async () => {
    setSaving(true);
    try {
      const heads = rankedHeads[activeTab];
      const shafts = rankedShafts[activeTab];

      const headRankings = heads.map(h => ({
        brand: h.head_brand,
        model: h.head_model,
        loft: h.head_loft,
        rank: h.rank,
        score: h.score,
      }));

      const shaftRankings = shafts.map(s => ({
        brand: s.shaft_brand,
        model: s.shaft_model,
        flex: s.shaft_flex,
        weight: s.shaft_weight,
        rank: s.rank,
        score: s.score,
      }));

      const hasLm = lmEntries.length > 0 && lmEntries.some(e =>
        Object.values(e.data).some(v => v !== undefined && v !== null)
      );

      const payload = {
        profile_id: pid,
        equipment_type: activeTab,
        has_launch_monitor: hasLm,
        launch_monitor_data: hasLm ? lmEntries : undefined,
        head_rankings_json: JSON.stringify(headRankings),
        shaft_rankings_json: JSON.stringify(shaftRankings),
        // Use top-ranked head as the "tried" fields for backwards compat
        tried_head_brand: heads[0]?.head_brand,
        tried_head_model: heads[0]?.head_model,
        tried_head_loft: heads[0]?.head_loft,
        tried_shaft_brand: shafts[0]?.shaft_brand,
        tried_shaft_model: shafts[0]?.shaft_model,
        tried_shaft_flex: shafts[0]?.shaft_flex,
        tried_shaft_weight: shafts[0]?.shaft_weight,
        user_rating: heads[0]?.score,
      } as any;

      const saved = await createReview(payload);
      setExistingReviews(prev => [...prev, saved]);
      toast.success(`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} review saved!`);
      logEvent({
        session_id: sessionIdRef.current,
        event_type: 'post_fit_review_rating_submitted',
        metadata: {
          club_category: activeTab,
          head_count: heads.length,
          shaft_count: shafts.length,
          has_launch_monitor: hasLm,
        },
      });
      setPhase('select');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  // ─── Phase 1 UI ─────────────────────────────────────────────────────────────

  const renderSelectPhase = () => {
    const heads = tabHeads[activeTab];
    const shafts = availableShafts[activeTab];
    const selHeads = selectedHeadKeys[activeTab];
    const selShafts = selectedShaftKeys[activeTab];
    const canContinue = selHeads.size > 0 && (shafts.length === 0 || selShafts.size > 0);

    return (
      <div className="space-y-8">
        {/* Heads */}
        <div>
          <div className="flex items-baseline gap-2 mb-3">
            <h3 className="text-base font-semibold text-gray-900">Select Heads You Tried</h3>
            <span className="text-sm text-gray-500">({selHeads.size}/5 selected)</span>
          </div>
          {heads.length === 0 ? (
            <div className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">
              No head recommendations available.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {heads.map(head => {
                const selected = selHeads.has(head.key);
                const disabled = !selected && selHeads.size >= 5;
                return (
                  <button
                    key={head.key}
                    onClick={() => !disabled && toggleHead(activeTab, head.key)}
                    disabled={disabled}
                    className={`text-left border-2 rounded-lg p-3 transition-colors ${
                      selected
                        ? 'border-green-ink bg-green/10'
                        : disabled
                        ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                        : 'border-gray-200 hover:border-green-ink hover:bg-green/10'
                    }`}
                  >
                    <div className="font-medium text-gray-900 text-sm">
                      {head.head_brand} {head.head_model}
                      {head.head_loft ? ` ${head.head_loft}°` : ''}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      {head.head_score !== undefined ? (
                        <span className="text-xs font-semibold text-green-ink bg-green/15 px-2 py-0.5 rounded">
                          Score: {(head.head_score * 10).toFixed(0)}/100
                        </span>
                      ) : <span />}
                      {selected && (
                        <span className="text-xs text-green-ink font-medium">✓ Selected</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Shafts (only for driver/iron — wedge has a text recommendation) */}
        {activeTab !== 'wedge' && (
          <div>
            <div className="flex items-baseline gap-2 mb-3">
              <h3 className="text-base font-semibold text-gray-900">Select Shafts You Tried</h3>
              <span className="text-sm text-gray-500">({selShafts.size}/5 selected)</span>
              {selHeads.size === 0 && (
                <span className="text-xs text-amber-600">— select a head first to see shafts</span>
              )}
            </div>
            {shafts.length === 0 ? (
              <div className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">
                {selHeads.size === 0
                  ? 'Select a head above to see available shafts.'
                  : 'No shaft recommendations for the selected head(s).'}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {shafts.map(shaft => {
                  const selected = selShafts.has(shaft.key);
                  const disabled = !selected && selShafts.size >= 5;
                  return (
                    <button
                      key={shaft.key}
                      onClick={() => !disabled && toggleShaft(activeTab, shaft.key)}
                      disabled={disabled}
                      className={`text-left border-2 rounded-lg p-3 transition-colors ${
                        selected
                          ? 'border-blue-500 bg-blue-50'
                          : disabled
                          ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                          : 'border-gray-200 hover:border-blue-400 hover:bg-blue-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900 text-sm">
                        {shaft.shaft_brand} {shaft.shaft_model}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {shaft.shaft_flex}
                        {shaft.shaft_weight ? ` · ${shaft.shaft_weight}g` : ''}
                      </div>
                      {selected && (
                        <span className="text-xs text-blue-600 font-medium mt-1 block">✓ Selected</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Continue button */}
        <div className="pt-2">
          <button
            onClick={proceedToRank}
            disabled={!canContinue}
            className="px-6 py-2.5 bg-green-ink text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Continue to Ranking →
          </button>
          {!canContinue && selHeads.size === 0 && (
            <p className="text-xs text-gray-500 mt-2">Select at least one head to continue.</p>
          )}
        </div>
      </div>
    );
  };

  // ─── Phase 2 UI ─────────────────────────────────────────────────────────────

  const renderRankPhase = () => {
    const heads = rankedHeads[activeTab];
    const shafts = rankedShafts[activeTab];

    return (
      <div className="space-y-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPhase('select')}
            className="text-sm text-green-ink hover:text-green-ink font-medium"
          >
            ← Back to Selection
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Heads leaderboard */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
              Heads Leaderboard
            </h3>
            <p className="text-xs text-gray-500 mb-3">Drag to reorder. Click a number to set score.</p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => handleHeadDragEnd(e, activeTab)}
            >
              <SortableContext
                items={heads.map(h => h.key)}
                strategy={verticalListSortingStrategy}
              >
                {heads.map((head) => (
                  <SortableItem
                    key={head.key}
                    id={head.key}
                    rank={head.rank}
                    label={`${head.head_brand} ${head.head_model}${head.head_loft ? ` ${head.head_loft}°` : ''}`}
                    sublabel=""
                    score={head.score}
                    onScoreChange={(score) => updateHeadScore(activeTab, head.key, score)}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {heads.length === 0 && (
              <div className="text-sm text-gray-400 text-center py-4">No heads selected.</div>
            )}
          </div>

          {/* Shafts leaderboard */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
              Shafts Leaderboard
            </h3>
            <p className="text-xs text-gray-500 mb-3">Drag to reorder. Click a number to set score.</p>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e) => handleShaftDragEnd(e, activeTab)}
            >
              <SortableContext
                items={shafts.map(s => s.key)}
                strategy={verticalListSortingStrategy}
              >
                {shafts.map((shaft) => (
                  <SortableItem
                    key={shaft.key}
                    id={shaft.key}
                    rank={shaft.rank}
                    label={`${shaft.shaft_brand} ${shaft.shaft_model}`}
                    sublabel={`${shaft.shaft_flex}${shaft.shaft_weight ? ` · ${shaft.shaft_weight}g` : ''}`}
                    score={shaft.score}
                    onScoreChange={(score) => updateShaftScore(activeTab, shaft.key, score)}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {shafts.length === 0 && activeTab === 'wedge' && (
              <div className="text-sm text-gray-400 text-center py-4">
                Wedge shafts are not individually selectable.
              </div>
            )}
            {shafts.length === 0 && activeTab !== 'wedge' && (
              <div className="text-sm text-gray-400 text-center py-4">No shafts selected.</div>
            )}
          </div>
        </div>

        {/* LM data — per-club entries (up to 5) */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => setShowLmForm(f => !f)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <span>
              Launch Monitor Data
              {lmEntries.length > 0 && (
                <span className="ml-2 text-xs text-green-ink font-normal">
                  {lmEntries.length} club{lmEntries.length !== 1 ? 's' : ''} recorded
                </span>
              )}
            </span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-4 w-4 text-gray-500 transition-transform ${showLmForm ? 'rotate-180' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showLmForm && (
            <div className="px-4 pb-4 border-t border-gray-100 space-y-4 mt-4">
              {/* Existing LM entries */}
              {lmEntries.map((entry, idx) => {
                const clubOptions = heads.map(h => ({
                  key: h.key,
                  label: `${h.head_brand} ${h.head_model}${h.head_loft ? ` ${h.head_loft}°` : ''}`,
                }));
                return (
                  <div key={entry.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Club {idx + 1}
                        </span>
                        <select
                          value={entry.clubKey}
                          onChange={e => {
                            const opt = clubOptions.find(o => o.key === e.target.value);
                            setLmEntries(prev => prev.map(en =>
                              en.id === entry.id
                                ? { ...en, clubKey: e.target.value, clubLabel: opt?.label || e.target.value }
                                : en
                            ));
                          }}
                          className="text-sm border border-gray-300 rounded px-2 py-1 bg-white focus:border-green-ink focus:ring-green-ink"
                        >
                          <option value="">— select club —</option>
                          {clubOptions.map(o => (
                            <option key={o.key} value={o.key}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={() => setLmEntries(prev => prev.filter(en => en.id !== entry.id))}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                    <PostFittingLaunchMonitor
                      equipmentType={activeTab}
                      clubLabel={entry.clubLabel}
                      data={entry.data}
                      onChange={data => setLmEntries(prev =>
                        prev.map(en => en.id === entry.id ? { ...en, data } : en)
                      )}
                    />
                  </div>
                );
              })}

              {/* Add club button */}
              {lmEntries.length < 5 && (
                <button
                  onClick={() => {
                    const firstHead = heads[0];
                    const label = firstHead
                      ? `${firstHead.head_brand} ${firstHead.head_model}${firstHead.head_loft ? ` ${firstHead.head_loft}°` : ''}`
                      : '';
                    setLmEntries(prev => [
                      ...prev,
                      {
                        id: `lm-${Date.now()}-${Math.random()}`,
                        clubKey: firstHead?.key || '',
                        clubLabel: label,
                        data: {},
                      },
                    ]);
                  }}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-green-ink hover:text-green-ink transition-colors"
                >
                  + Add Club's Launch Monitor Data {lmEntries.length > 0 ? `(${lmEntries.length}/5)` : ''}
                </button>
              )}
              {lmEntries.length === 0 && (
                <p className="text-xs text-gray-400 text-center">
                  Add LM data for each club you hit on the launch monitor. Select the specific club from the dropdown.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Save */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 pt-2">
          <button
            onClick={handleSave}
            disabled={saving || hasExistingReview(activeTab)}
            className="px-6 py-2.5 bg-green-ink text-white font-medium rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving
              ? 'Saving...'
              : hasExistingReview(activeTab)
              ? 'Already Submitted'
              : 'Save Review'}
          </button>
          {hasExistingReview(activeTab) && (
            <span className="text-sm text-gray-500 leading-tight">
              Already submitted for {activeTab}.
            </span>
          )}
        </div>
      </div>
    );
  };

  // ─── Existing reviews display ─────────────────────────────────────────────

  const renderExistingReview = (review: PostFittingReview) => {
    let headRankings: any[] = [];
    let shaftRankings: any[] = [];
    try {
      if (review.head_rankings_json) headRankings = JSON.parse(review.head_rankings_json as any);
      if (review.shaft_rankings_json) shaftRankings = JSON.parse(review.shaft_rankings_json as any);
    } catch (_) {}

    return (
      <div key={review.id} className="border border-green-ink bg-green/10 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <span className="inline-block px-2 py-0.5 bg-green/15 text-green-ink text-xs font-medium rounded">
            Submitted
          </span>
          <span className="text-xs text-gray-500">{review.created_at?.slice(0, 10)}</span>
        </div>

        {headRankings.length > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-600 mb-1">Head Rankings</div>
            <div className="space-y-1">
              {headRankings.map((h: any) => (
                <div key={h.rank} className="flex items-center gap-2 text-sm">
                  <span className="text-xs text-gray-400 w-5">#{h.rank}</span>
                  <span className="text-gray-800 font-medium">{h.brand} {h.model}{h.loft ? ` ${h.loft}°` : ''}</span>
                  <span className="ml-auto text-green-ink font-semibold">{h.score}/10</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {shaftRankings.length > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-600 mb-1">Shaft Rankings</div>
            <div className="space-y-1">
              {shaftRankings.map((s: any) => (
                <div key={s.rank} className="flex items-center gap-2 text-sm">
                  <span className="text-xs text-gray-400 w-5">#{s.rank}</span>
                  <span className="text-gray-800 font-medium">{s.brand} {s.model}</span>
                  <span className="text-gray-500 text-xs">{s.flex}{s.weight ? ` ${s.weight}g` : ''}</span>
                  <span className="ml-auto text-blue-700 font-semibold">{s.score}/10</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {review.has_launch_monitor && (
          <div className="text-xs text-gray-500">
            LM Data:{review.launch_angle && ` Launch: ${review.launch_angle}°`}
            {review.spin_rate && ` | Spin: ${review.spin_rate} rpm`}
            {review.carry_distance && ` | Carry: ${review.carry_distance} yds`}
          </div>
        )}
      </div>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ProfessionalHeader user={user} onLogout={logout} />
        <main className="max-w-5xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-ink mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading fitting data...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ProfessionalHeader user={user} onLogout={logout} />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-2">
              <div>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-green-ink hover:text-green-ink font-medium mb-2 flex items-center text-sm"
                >
                  ← Back to Dashboard
                </button>
                <h1 className="text-2xl font-bold text-gray-900">Post-Fitting Review</h1>
                <p className="text-gray-600 mt-1 text-sm">
                  {phase === 'select'
                    ? 'Select the heads and shafts you tried, then rank them.'
                    : 'Drag to rank. Click a number to score (1=poor, 10=perfect).'}
                </p>
              </div>
              <span className="text-xs text-gray-400 flex-shrink-0">#{pid}</span>
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 mb-6">
            {(['driver', 'iron', 'wedge'] as EquipmentTab[]).map(tab => (
              <button
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setPhase('select');
                  setLmEntries([]);
                  setShowLmForm(false);
                }}
                className={`px-4 py-2.5 sm:px-6 sm:py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-green-ink text-green-ink'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
                {hasExistingReview(tab) && (
                  <span className="ml-2 inline-block w-2 h-2 bg-green/100 rounded-full align-middle"></span>
                )}
              </button>
            ))}
          </div>

          {/* Existing reviews for this tab */}
          {existingReviews.filter(r => r.equipment_type === activeTab).length > 0 && (
            <div className="mb-6 space-y-3">
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">Your Reviews</h3>
              {existingReviews.filter(r => r.equipment_type === activeTab).map(renderExistingReview)}
            </div>
          )}

          {/* Phase content */}
          {!hasExistingReview(activeTab) && (
            phase === 'select' ? renderSelectPhase() : renderRankPhase()
          )}
        </div>
      </main>
    </div>
  );
};

export default PostFittingReviewPage;
